/** @format */

import React, { useState, useEffect, Suspense, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Trash2,
  Edit,
  X,
  Calendar as CalendarIcon,
  ExternalLink,
  MapPin,
  Clock,
  User,
  FileText,
  ChevronRight,
  ChevronDown,
} from "lucide-react";
import { supabase } from "../../../lib/supabaseClient";
import { useNavigate } from "react-router-dom";
import "jspdf-autotable";
import { Canvas } from "@react-three/fiber";
import {
  OrbitControls,
  useGLTF,
  Stage,
  Environment,
  Center,
  Loader,
} from "@react-three/drei";

useGLTF.preload("/models/2020__mclaren_sabre.glb");

function Model() {
  const { scene } = useGLTF(
    "/models/2022__lamborghini_huracan_super_trofeo_evo2.glb"
  );
  return (
    <Center>
      <primitive
        object={scene}
        scale={[0.015, 0.015, 0.015]}
        position={[0, -1, 0]}
        rotation={[0, Math.PI / 4, 0]}
      />
    </Center>
  );
}

function CarScene() {
  return (
    <Canvas
      shadows
      camera={{ position: [6, 3, 6], fov: 45 }}
      className='fixed inset-0 -z-10'>
      <color attach='background' args={["#1e1e2e"]} />
      <Suspense fallback={null}>
        <Stage environment='city'>
          <Model />
        </Stage>
        <Environment preset='city' />
        <OrbitControls
          autoRotate
          autoRotateSpeed={0.5}
          enableZoom={false}
          enablePan={false}
        />
      </Suspense>
    </Canvas>
  );
}

const Calendar = () => {
  const navigate = useNavigate();

  // États
  const [formateurs, setFormateurs] = useState(["Sélectionner un formateur"]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [reunions, setReunions] = useState([]);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventType, setEventType] = useState("intervention");
  const [userRole, setUserRole] = useState(null);
  const [newEvent, setNewEvent] = useState({
    immatriculation: "",
    title: "",
    description: "",
    date: "",
    time: "",
    formateur: "Sélectionner un formateur",
    section: "Sélectionner une section",
    motif: "Sélectionner un motif",
    objet: "",
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedReunion, setExpandedReunion] = useState(null);

  const sections = [
    "Sélectionner une section",
    "CAP 1 A MVA",
    "CAP 1 B MVA",
    "CAP 2 A MVA",
    "CAP 2 B MVA",
    "CAP 1 CA",
    "CAP 2 CA",
    "CAP 1 PA",
    "BCP 1/3 MVA",
    "BCP 2/3 A MVA",
    "BCP 2/3 B MVA",
    "BCP 3/3 A MVA",
    "BCP 3/3 B MVA",
    "CQP TAVA 2024/26",
  ];

  const motifsReunion = [
    "Sélectionner un motif",
    "Réunion pédagogique",
    "Réunion de coordination",
    "Réunion de suivi",
    "Réunion d'évaluation",
    "Réunion extraordinaire",
  ];

  const monthNames = [
    "Janvier",
    "Février",
    "Mars",
    "Avril",
    "Mai",
    "Juin",
    "Juillet",
    "Août",
    "Septembre",
    "Octobre",
    "Novembre",
    "Décembre",
  ];
  const dayNames = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];

  const handleOpenFullIntervention = useCallback(() => {
    if (!selectedEvent) {
      console.error("Aucun événement sélectionné");
      return;
    }

    if (selectedEvent.type !== "intervention") {
      console.error("Ce n'est pas une intervention");
      return;
    }

    try {
      console.log("🚀 Navigation vers intervention form avec:", selectedEvent);

      navigate("/pole-auto/interventions/form", {
        state: {
          fromCalendar: true,
          calendarData: {
            immatriculation: selectedEvent.immatriculation || "",
            title: selectedEvent.title || "",
            description: selectedEvent.description || "",
            date: selectedEvent.date || "",
            time: selectedEvent.time || "",
            formateur: selectedEvent.formateur || "",
            section: selectedEvent.section || "",
            calendarEventId: selectedEvent.id || "",
          },
        },
        replace: false,
      });
    } catch (err) {
      console.error("❌ Erreur navigation:", err);
      alert("Erreur lors de la navigation vers l'intervention");
    }
  }, [selectedEvent, navigate]);

  // Charger les formateurs
  useEffect(() => {
    const fetchFormateurs = async () => {
      try {
        const { data, error } = await supabase
          .from("formateurs")
          .select("nom, prenom")
          .eq("actif", true)
          .order("nom");

        if (error) throw error;

        const formateursList = ["Sélectionner un formateur"];
        data?.forEach((f) => {
          formateursList.push(`${f.prenom} ${f.nom}`);
        });

        setFormateurs(formateursList);
      } catch (error) {
        console.error("Erreur chargement formateurs:", error);
      }
    };

    fetchFormateurs();
  }, []);

  // Charger le rôle de l'utilisateur
  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();

        if (user) {
          const { data, error } = await supabase
            .from("profiles")
            .select("role")
            .eq("id", user.id)
            .single();

          if (error) throw error;
          if (data) {
            setUserRole(data.role);
          }
        }
      } catch (error) {
        console.error("Erreur chargement rôle:", error);
      }
    };

    fetchUserRole();
  }, []);

  // Vérifier si administrateur
  const isAdmin = useCallback(() => {
    return userRole === "super_admin" || userRole === "administrateur";
  }, [userRole]);

  // Trier et classifier les événements
  const sortAndClassifyEvents = (allEvents) => {
    const sorted = [...allEvents].sort((a, b) => {
      const dateCompare = a.date.localeCompare(b.date);
      if (dateCompare !== 0) return dateCompare;
      return a.time.localeCompare(b.time);
    });

    const interventionsArray = sorted.filter(
      (event) => event.type === "intervention"
    );
    const reunionsArray = sorted.filter((event) => event.type === "reunion");

    setInterventions(interventionsArray);
    setReunions(reunionsArray);

    return sorted;
  };

  // Charger tous les événements
  const fetchAllEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from("calendar_events")
        .select("*")
        .order("date", { ascending: true });

      if (error) throw error;

      const sortedEvents = sortAndClassifyEvents(data || []);
      setEvents(sortedEvents);
    } catch (error) {
      console.error("Erreur chargement événements:", error.message);
      setError(
        "Impossible de charger les événements. Veuillez réessayer plus tard."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  // Chargement initial
  useEffect(() => {
    fetchAllEvents();
  }, [fetchAllEvents]);

  // Validation immatriculation
  const validateImmatriculation = (immat) => {
    const regex = /^[A-Z]{2}-\d{3}-[A-Z]{2}$/;
    return regex.test(immat);
  };

  // Format immatriculation
  const formatImmatriculation = (value) => {
    let cleaned = value.replace(/[^A-Za-z0-9]/g, "").toUpperCase();

    if (cleaned.length >= 2 && cleaned.length < 5) {
      cleaned = cleaned.slice(0, 2) + "-" + cleaned.slice(2);
    } else if (cleaned.length >= 5) {
      cleaned =
        cleaned.slice(0, 2) +
        "-" +
        cleaned.slice(2, 5) +
        (cleaned.length > 5 ? "-" + cleaned.slice(5, 7) : "");
    }

    return cleaned;
  };

  const handleImmatriculationChange = (e) => {
    const formatted = formatImmatriculation(e.target.value);
    setNewEvent({ ...newEvent, immatriculation: formatted });
  };

  // Export PDF
  const handleExportPDF = async () => {
    if (!isAdmin()) {
      alert("Vous devez être administrateur pour exporter les événements");
      return;
    }

    try {
      // ✅ Import dynamique de jsPDF et jspdf-autotable
      const { jsPDF } = await import('jspdf');
      await import('jspdf-autotable');

      const doc = new jsPDF();
      let yPosition = 20;

      // Titre
      doc.setFontSize(18);
      doc.text("Calendrier des Événements - Pôle Automobile", 14, yPosition);
      yPosition += 10;

      // Date de génération
      doc.setFontSize(12);
      doc.text(`Généré le: ${new Date().toLocaleDateString("fr-FR")}`, 14, yPosition);
      yPosition += 15;

      // Section Interventions
      doc.setFontSize(14);
      doc.setTextColor(30, 64, 175);
      doc.text("1. INTERVENTIONS", 14, yPosition);
      doc.setTextColor(0, 0, 0);
      yPosition += 10;

      if (interventions.length > 0) {
        doc.setFontSize(10);
        interventions.forEach((event) => {
          if (yPosition > 270) {
            doc.addPage();
            yPosition = 20;
          }

          doc.setFont(undefined, "bold");
          doc.text(`${formatDateToHuman(event.date)} - ${event.time}`, 14, yPosition);
          yPosition += 7;

          doc.setFont(undefined, "normal");
          doc.text(`Titre: ${event.title}`, 14, yPosition);
          yPosition += 5;
          doc.text(`Immatriculation: ${event.immatriculation || "N/A"}`, 14, yPosition);
          yPosition += 5;
          doc.text(`Formateur: ${event.formateur || "N/A"}`, 14, yPosition);
          yPosition += 5;
          doc.text(`Section: ${event.section || "N/A"}`, 14, yPosition);
          yPosition += 5;

          if (event.description) {
            doc.text(`Description: ${event.description}`, 14, yPosition, { maxWidth: 180 });
            yPosition += 10;
          }

          yPosition += 5;
        });
      } else {
        doc.text("Aucune intervention", 14, yPosition);
        yPosition += 10;
      }

      yPosition += 10;

      // Section Réunions
      doc.setFontSize(14);
      doc.setTextColor(109, 40, 217);
      doc.text("2. RÉUNIONS", 14, yPosition);
      doc.setTextColor(0, 0, 0);
      yPosition += 10;

      if (reunions.length > 0) {
        doc.setFontSize(10);
        reunions.forEach((reunion) => {
          if (yPosition > 270) {
            doc.addPage();
            yPosition = 20;
          }

          doc.setFont(undefined, "bold");
          doc.text(`${formatDateToHuman(reunion.date)} - ${reunion.time}`, 14, yPosition);
          yPosition += 7;

          doc.setFont(undefined, "normal");
          doc.text(`Titre: ${reunion.title}`, 14, yPosition);
          yPosition += 5;
          doc.text(`Motif: ${reunion.motif || "N/A"}`, 14, yPosition);
          yPosition += 5;
          doc.text(`Responsable: ${reunion.formateur || "N/A"}`, 14, yPosition);
          yPosition += 5;

          if (reunion.objet) {
            doc.text(`Objet: ${reunion.objet}`, 14, yPosition, { maxWidth: 180 });
            yPosition += 10;
          }

          yPosition += 5;
        });
      } else {
        doc.text("Aucune réunion", 14, yPosition);
      }

      // Numérotation des pages
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(10);
        doc.text(
          `Page ${i} sur ${pageCount}`,
          14,
          doc.internal.pageSize.height - 10
        );
      }

      const fileName = `calendrier_events_${new Date().toLocaleDateString("fr-FR").replace(/\//g, "-")}.pdf`;

      // Modal de choix
      const action = window.confirm(
        "📄 PDF généré avec succès !\n\n" +
        "OK = Afficher le PDF\n" +
        "Annuler = Télécharger le fichier"
      );

      if (action) {
        doc.output('dataurlnewwindow');
      } else {
        doc.save(fileName);
      }
    } catch (error) {
      console.error("❌ Erreur PDF:", error);
      alert(`Erreur lors de la génération du PDF: ${error.message}`);
    }
  };

  // Ajouter/Modifier événement
  const handleAddEvent = async (e) => {
    e.preventDefault();

    try {
      let eventData;

      if (eventType === "intervention") {
        if (!validateImmatriculation(newEvent.immatriculation)) {
          alert(
            "Format d'immatriculation invalide. Utilisez le format XX-000-XX"
          );
          return;
        }

        eventData = {
          type: "intervention",
          title: newEvent.title,
          date: newEvent.date,
          time: newEvent.time,
          formateur: newEvent.formateur,
          immatriculation: newEvent.immatriculation,
          section: newEvent.section,
          description: newEvent.description,
        };
      } else {
        eventData = {
          type: "reunion",
          title: newEvent.title,
          date: newEvent.date,
          time: newEvent.time,
          formateur: newEvent.formateur,
          motif: newEvent.motif,
          objet: newEvent.objet,
        };
      }

      if (selectedEvent) {
        const { error } = await supabase
          .from("calendar_events")
          .update(eventData)
          .eq("id", selectedEvent.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("calendar_events")
          .insert([eventData]);

        if (error) throw error;
      }

      await fetchAllEvents();
      resetForm();
    } catch (error) {
      console.error("Erreur sauvegarde:", error.message);
      alert("Erreur lors de la sauvegarde");
    }
  };

  // Réinitialiser le formulaire
  const resetForm = () => {
    setNewEvent({
      immatriculation: "",
      title: "",
      description: "",
      date: "",
      time: "",
      formateur: "Sélectionner un formateur",
      section: "Sélectionner une section",
      motif: "Sélectionner un motif",
      objet: "",
    });
    setEventType("intervention");
    setShowEventModal(false);
    setSelectedEvent(null);
  };

  // Supprimer événement
  const handleDeleteEvent = async (eventId) => {
    try {
      const { error } = await supabase
        .from("calendar_events")
        .delete()
        .eq("id", eventId);

      if (error) throw error;

      await fetchAllEvents();
      setShowEventModal(false);
    } catch (error) {
      console.error("Erreur suppression:", error.message);
      alert("Erreur lors de la suppression");
    }
  };

  // Utilitaires calendrier
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() - 1)));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() + 1)));
  };

  // ✅ CORRECTION: Récupérer TOUS les événements pour une date (interventions + réunions)
  const getEventsForDate = (date) => {
    return events.filter((event) => event.date === date);
  };

  const formatDateToHuman = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const getFormattedDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getCurrentMonthReunions = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() + 1;
    const monthString = month < 10 ? `0${month}` : `${month}`;

    return reunions.filter((reunion) => {
      return reunion.date.startsWith(`${year}-${monthString}`);
    });
  };

  const renderCalendarDays = () => {
    const days = [];
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);

    for (let i = 0; i < firstDay; i++) {
      days.push(
        <div
          key={`empty-${i}`}
          className='p-2 border border-slate-600 bg-slate-800/60 backdrop-blur-sm'
        />
      );
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = `${currentDate.getFullYear()}-${String(
        currentDate.getMonth() + 1
      ).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      
      // ✅ Récupérer TOUS les événements pour cette date
      const dayEvents = getEventsForDate(date);
      const hasEvents = dayEvents.length > 0;

      days.push(
        <motion.div
          key={day}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.2 }}
          className={`min-h-24 p-2 border border-slate-600 hover:bg-slate-700/50 cursor-pointer transition-colors relative backdrop-blur-sm ${
            hasEvents ? "bg-slate-800/70" : "bg-slate-800/60"
          }`}
          onClick={() => {
            setSelectedEvent(null);
            setEventType("intervention");
            setNewEvent({
              ...newEvent,
              date: date,
            });
            setShowEventModal(true);
          }}>
          <span className='font-medium text-white'>{day}</span>
          {hasEvents && (
            <div className='mt-1 space-y-1'>
              {dayEvents.map((event) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className={`text-xs p-1 rounded truncate ${
                    event.type === 'intervention' 
                      ? 'bg-blue-500/20 text-blue-200 hover:bg-blue-500/30 border-l-2 border-blue-400'
                      : 'bg-purple-500/20 text-purple-200 hover:bg-purple-500/30 border-l-2 border-purple-400'
                  } cursor-pointer transition-colors`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedEvent(event);
                    setEventType(event.type);
                    setNewEvent({
                      ...event,
                      formateur: event.formateur || "Sélectionner un formateur",
                      section: event.section || "Sélectionner une section",
                      motif: event.motif || "Sélectionner un motif",
                    });
                    setShowEventModal(true);
                  }}
                  title={`${event.time} - ${event.title}`}
                  whileHover={{ scale: 1.02 }}>
                  <div className='flex items-center'>
                    <span className='mr-1 opacity-80'>
                      {event.type === 'intervention' ? '🔧' : '📅'}
                    </span>
                    <span>
                      {event.time} - {event.title}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      );
    }

    return days;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2,
      },
    },
  };

  const groupReunionsByDate = () => {
    const groupedReunions = {};

    getCurrentMonthReunions().forEach((reunion) => {
      if (!groupedReunions[reunion.date]) {
        groupedReunions[reunion.date] = [];
      }
      groupedReunions[reunion.date].push(reunion);
    });

    return Object.entries(groupedReunions)
      .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
      .map(([date, reunions]) => ({
        date,
        humanDate: formatDateToHuman(date),
        reunions,
      }));
  };

  const renderReunionsList = () => {
    const groupedReunions = groupReunionsByDate();

    if (groupedReunions.length === 0) {
      return (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className='text-center py-8 text-slate-400'>
          <p>Aucune réunion ce mois-ci</p>
          <button
            onClick={() => {
              setSelectedEvent(null);
              setEventType("reunion");
              setNewEvent({
                ...newEvent,
                date: getFormattedDate(new Date()),
              });
              setShowEventModal(true);
            }}
            className='mt-4 text-sm text-blue-400 hover:text-blue-300 flex items-center justify-center mx-auto'>
            <Plus className='w-4 h-4 mr-1' />
            Planifier une réunion
          </button>
        </motion.div>
      );
    }

    return (
      <div className='space-y-4'>
        {groupedReunions.map((group) => (
          <motion.div
            key={group.date}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className='border-b border-slate-700 pb-3'>
            <p className='text-sm font-medium text-blue-300 mb-2'>
              {group.humanDate}
            </p>

            {group.reunions.map((reunion) => (
              <motion.div
                key={reunion.id}
                whileHover={{ scale: 1.01 }}
                className='bg-purple-500/10 hover:bg-purple-500/20 rounded-lg p-3 mb-2 border-l-2 border-purple-400 cursor-pointer transition-all'
                onClick={() =>
                  setExpandedReunion(
                    expandedReunion === reunion.id ? null : reunion.id
                  )
                }>
                <div className='flex justify-between items-center'>
                  <h4 className='font-medium text-white'>{reunion.title}</h4>
                  <div className='flex items-center'>
                    <span className='text-xs text-purple-200 bg-purple-500/30 rounded px-2 py-1 mr-2'>
                      {reunion.time}
                    </span>
                    {expandedReunion === reunion.id ?
                      <ChevronDown className='w-4 h-4 text-purple-300' />
                    : <ChevronRight className='w-4 h-4 text-purple-300' />}
                  </div>
                </div>

                <AnimatePresence>
                  {expandedReunion === reunion.id && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className='mt-3 overflow-hidden'>
                      <div className='grid grid-cols-1 gap-2 text-sm'>
                        <div className='flex items-start'>
                          <User className='w-4 h-4 mr-2 text-purple-300 mt-0.5' />
                          <div>
                            <p className='text-slate-300'>Responsable:</p>
                            <p className='text-white'>{reunion.formateur}</p>
                          </div>
                        </div>

                        <div className='flex items-start'>
                          <FileText className='w-4 h-4 mr-2 text-purple-300 mt-0.5' />
                          <div>
                            <p className='text-slate-300'>Motif:</p>
                            <p className='text-white'>{reunion.motif}</p>
                          </div>
                        </div>

                        {reunion.objet && (
                          <div className='flex items-start'>
                            <FileText className='w-4 h-4 mr-2 text-purple-300 mt-0.5' />
                            <div>
                              <p className='text-slate-300'>Objet:</p>
                              <p className='text-white'>{reunion.objet}</p>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className='flex space-x-2 mt-3'>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedEvent(reunion);
                            setEventType("reunion");
                            setNewEvent({
                              ...reunion,
                              formateur:
                                reunion.formateur ||
                                "Sélectionner un formateur",
                              motif: reunion.motif || "Sélectionner un motif",
                            });
                            setShowEventModal(true);
                          }}
                          className='text-xs bg-slate-700 hover:bg-slate-600 text-white px-3 py-1 rounded flex items-center'>
                          <Edit className='w-3 h-3 mr-1' />
                          Modifier
                        </motion.button>

                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteEvent(reunion.id);
                          }}
                          className='text-xs bg-red-700/50 hover:bg-red-700 text-white px-3 py-1 rounded flex items-center'>
                          <Trash2 className='w-3 h-3 mr-1' />
                          Supprimer
                        </motion.button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </motion.div>
        ))}
      </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className='min-h-screen py-40'>
      <div className='fixed inset-0 z-0'>
        <CarScene />
      </div>
      <Loader />

      <div className='container mx-auto px-4 mb-32 relative z-10'>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.8,
            ease: [0.4, 0, 0.2, 1],
          }}
          className='text-center mb-20'>
          <motion.h1
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className='text-4xl md:text-6xl font-bold text-center mb-8 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500'>
            Calendrier des Interventions
          </motion.h1>

          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className='text-xl text-center text-white font-medium max-w-2xl mx-auto mb-12 bg-slate-800/60 p-6 rounded-lg backdrop-blur-sm'>
            Gestion des rendez-vous, interventions et réunions pour le Pôle
            Automobile
          </motion.p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className='flex justify-center gap-4 mb-10'>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              setSelectedEvent(null);
              setEventType("intervention");
              setNewEvent({
                ...newEvent,
                date: getFormattedDate(new Date()),
              });
              setShowEventModal(true);
            }}
            className='px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center justify-center space-x-2'>
            <Plus className='w-5 h-5' />
            <span>Nouvelle intervention</span>
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              setSelectedEvent(null);
              setEventType("reunion");
              setNewEvent({
                ...newEvent,
                date: getFormattedDate(new Date()),
              });
              setShowEventModal(true);
            }}
            className='px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center justify-center space-x-2'>
            <Plus className='w-5 h-5' />
            <span>Nouvelle réunion</span>
          </motion.button>
        </motion.div>

        {loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className='text-center py-8'>
            <div className='inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500'></div>
            <p className='mt-2 text-slate-300'>Chargement des événements...</p>
          </motion.div>
        )}

        {error && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className='bg-red-500/30 border border-red-500 text-white p-4 rounded-lg mb-6 mx-auto max-w-2xl'>
            <p>{error}</p>
            <button
              onClick={() => fetchAllEvents()}
              className='mt-2 bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg text-sm'>
              Réessayer
            </button>
          </motion.div>
        )}

        <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
          <motion.div
            variants={containerVariants}
            initial='hidden'
            animate='visible'
            className='lg:col-span-2 bg-slate-800/60 rounded-lg backdrop-blur-sm p-6 mb-8'>
            <div className='flex justify-between items-center mb-6 border-b border-slate-700 pb-4'>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handlePrevMonth}
                className='px-4 py-2 bg-slate-700/50 hover:bg-slate-700 text-white rounded-lg transition-colors flex items-center'>
                ← Mois précédent
              </motion.button>

              <h2 className='text-2xl font-bold text-white'>
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleNextMonth}
                className='px-4 py-2 bg-slate-700/50 hover:bg-slate-700 text-white rounded-lg transition-colors flex items-center'>
                Mois suivant →
              </motion.button>
            </div>

            <div className='mb-4 flex items-center'>
              <div className='p-2 bg-slate-700/50 rounded mr-3'>
                <CalendarIcon className='w-5 h-5 text-blue-400' />
              </div>
              <h3 className='text-xl font-semibold text-white'>
                Interventions & Réunions
              </h3>
            </div>

            <div className='grid grid-cols-7 gap-2 mb-2'>
              {dayNames.map((day) => (
                <div
                  key={day}
                  className='text-center font-medium text-blue-400 p-2 bg-slate-800/80 rounded'>
                  {day}
                </div>
              ))}
            </div>

            <div className='grid grid-cols-7 gap-2'>{renderCalendarDays()}</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
            className='bg-slate-800/60 rounded-lg backdrop-blur-sm p-6 h-fit sticky top-6'>
            <div className='flex items-center justify-between mb-6 border-b border-slate-700 pb-4'>
              <div className='flex items-center'>
                <div className='p-2 bg-slate-700/50 rounded mr-3'>
                  <FileText className='w-5 h-5 text-purple-400' />
                </div>
                <h3 className='text-xl font-semibold text-white'>
                  Réunions du mois
                </h3>
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setSelectedEvent(null);
                  setEventType("reunion");
                  setNewEvent({
                    ...newEvent,
                    date: getFormattedDate(new Date()),
                  });
                  setShowEventModal(true);
                }}
                className='p-2 bg-purple-600/30 hover:bg-purple-600/50 rounded-lg'>
                <Plus className='w-4 h-4 text-purple-200' />
              </motion.button>
            </div>

            <div className='overflow-auto max-h-[600px] pr-2 custom-scrollbar'>
              {renderReunionsList()}
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className='max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 mb-16 mt-8'>
          <motion.div
            whileHover={{ scale: 1.02 }}
            className='bg-slate-800/60 p-6 rounded-lg backdrop-blur-sm border-l-4 border-blue-500'>
            <h3 className='text-xl font-semibold text-white mb-3 flex items-center'>
              <span className='mr-2 p-2 bg-slate-700/50 rounded'>🔧</span>
              Interventions
            </h3>
            <p className='text-gray-300 text-sm leading-relaxed'>
              Les interventions sont des sessions de maintenance, réparation ou
              formation pratique pour les véhicules.
            </p>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            className='bg-slate-800/60 p-6 rounded-lg backdrop-blur-sm border-l-4 border-purple-500'>
            <h3 className='text-xl font-semibold text-white mb-3 flex items-center'>
              <span className='mr-2 p-2 bg-slate-700/50 rounded'>📅</span>
              Réunions
            </h3>
            <p className='text-gray-300 text-sm leading-relaxed'>
              Les réunions permettent de coordonner les activités entre
              formateurs et suivre les apprentis.
            </p>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          className='flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4 mb-20'>
          <motion.button
            onClick={handleExportPDF}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className='px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center justify-center space-x-2'>
            <span>Exporter les événements</span>
            <ExternalLink className='w-4 h-4' />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => fetchAllEvents()}
            className='px-8 py-4 border border-white text-white hover:bg-white hover:text-slate-900 rounded-lg transition-colors'>
            Actualiser le calendrier
          </motion.button>
        </motion.div>

        <AnimatePresence>
          {showEventModal && (
            <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4'>
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className='bg-slate-800 rounded-xl p-6 w-full max-w-xl max-h-[90vh] overflow-y-auto border border-slate-700 backdrop-blur-sm'>
                <div className='flex justify-between items-center mb-6 border-b border-slate-700 pb-4'>
                  <h3 className='text-xl font-bold text-white flex items-center'>
                    {selectedEvent ?
                      <>
                        <Edit className='w-5 h-5 mr-2 text-blue-400' />
                        Modifier l'événement
                      </>
                    : <>
                        <Plus className='w-5 h-5 mr-2 text-blue-400' />
                        Nouvel événement
                      </>
                    }
                  </h3>
                  <motion.button
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setShowEventModal(false)}
                    className='p-2 hover:bg-slate-700 rounded-full transition-colors'>
                    <X className='w-5 h-5' />
                  </motion.button>
                </div>

                <div className='mb-6'>
                  <div className='flex gap-4'>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type='button'
                      onClick={() => setEventType("intervention")}
                      className={`flex-1 py-3 px-4 rounded-lg transition-colors flex items-center justify-center ${
                        eventType === "intervention" ?
                          "bg-blue-500/30 text-white border-2 border-blue-500"
                        : "bg-slate-700/50 hover:bg-slate-600/50 border-2 border-transparent"
                      }`}>
                      <span className='mr-2'>🔧</span>
                      Intervention
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type='button'
                      onClick={() => setEventType("reunion")}
                      className={`flex-1 py-3 px-4 rounded-lg transition-colors flex items-center justify-center ${
                        eventType === "reunion" ?
                          "bg-purple-500/30 text-white border-2 border-purple-500"
                        : "bg-slate-700/50 hover:bg-slate-600/50 border-2 border-transparent"
                      }`}>
                      <span className='mr-2'>📅</span>
                      Réunion
                    </motion.button>
                  </div>
                </div>

                <form onSubmit={handleAddEvent} className='space-y-5'>
                  {eventType === "intervention" ?
                    <>
                      <div>
                        <label className='block text-sm font-medium mb-2 text-blue-300'>
                          Immatriculation
                        </label>
                        <input
                          type='text'
                          value={newEvent.immatriculation}
                          onChange={handleImmatriculationChange}
                          placeholder='XX-000-XX'
                          maxLength='9'
                          className='w-full bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 text-white'
                          required
                        />
                        <p className='text-xs text-slate-400 mt-1'>
                          Format: XX-000-XX
                        </p>
                      </div>

                      <div>
                        <label className='block text-sm font-medium mb-2 text-blue-300'>
                          Formateur
                        </label>
                        <select
                          value={newEvent.formateur}
                          onChange={(e) =>
                            setNewEvent({
                              ...newEvent,
                              formateur: e.target.value,
                            })
                          }
                          className='w-full bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 text-white'
                          required>
                          {formateurs.map((formateur) => (
                            <option
                              key={formateur}
                              value={formateur}
                              disabled={
                                formateur === "Sélectionner un formateur"
                              }>
                              {formateur}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className='block text-sm font-medium mb-2 text-blue-300'>
                          Section
                        </label>
                        <select
                          value={newEvent.section}
                          onChange={(e) =>
                            setNewEvent({
                              ...newEvent,
                              section: e.target.value,
                            })
                          }
                          className='w-full bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 text-white'
                          required>
                          {sections.map((section) => (
                            <option
                              key={section}
                              value={section}
                              disabled={section === "Sélectionner une section"}>
                              {section}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className='block text-sm font-medium mb-2 text-blue-300'>
                          Titre de l'intervention
                        </label>
                        <input
                          type='text'
                          value={newEvent.title}
                          onChange={(e) =>
                            setNewEvent({ ...newEvent, title: e.target.value })
                          }
                          className='w-full bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 text-white'
                          placeholder='Ex: Révision moteur'
                          required
                        />
                      </div>

                      <div>
                        <label className='block text-sm font-medium mb-2 text-blue-300'>
                          Description
                        </label>
                        <textarea
                          value={newEvent.description}
                          onChange={(e) =>
                            setNewEvent({
                              ...newEvent,
                              description: e.target.value,
                            })
                          }
                          className='w-full bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 text-white'
                          placeholder="Détails sur l'intervention"
                          rows='3'
                        />
                      </div>
                    </>
                  : <>
                      <div>
                        <label className='block text-sm font-medium mb-2 text-purple-300'>
                          Titre de la réunion
                        </label>
                        <input
                          type='text'
                          value={newEvent.title}
                          onChange={(e) =>
                            setNewEvent({ ...newEvent, title: e.target.value })
                          }
                          className='w-full bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-500 text-white'
                          placeholder='Ex: Réunion trimestrielle'
                          required
                        />
                      </div>

                      <div>
                        <label className='block text-sm font-medium mb-2 text-purple-300'>
                          Motif
                        </label>
                        <select
                          value={newEvent.motif}
                          onChange={(e) =>
                            setNewEvent({ ...newEvent, motif: e.target.value })
                          }
                          className='w-full bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-500 text-white'
                          required>
                          {motifsReunion.map((motif) => (
                            <option
                              key={motif}
                              value={motif}
                              disabled={motif === "Sélectionner un motif"}>
                              {motif}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className='block text-sm font-medium mb-2 text-purple-300'>
                          Formateur responsable
                        </label>
                        <select
                          value={newEvent.formateur}
                          onChange={(e) =>
                            setNewEvent({
                              ...newEvent,
                              formateur: e.target.value,
                            })
                          }
                          className='w-full bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-500 text-white'
                          required>
                          {formateurs.map((formateur) => (
                            <option
                              key={formateur}
                              value={formateur}
                              disabled={
                                formateur === "Sélectionner un formateur"
                              }>
                              {formateur}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className='block text-sm font-medium mb-2 text-purple-300'>
                          Objet de la réunion
                        </label>
                        <textarea
                          value={newEvent.objet}
                          onChange={(e) =>
                            setNewEvent({ ...newEvent, objet: e.target.value })
                          }
                          className='w-full bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-500 text-white'
                          placeholder="Détails sur l'objet de la réunion"
                          rows='3'
                          required
                        />
                      </div>
                    </>
                  }

                  <div className='grid grid-cols-2 gap-4'>
                    <div>
                      <label className='block text-sm font-medium mb-2 text-blue-300'>
                        Date
                      </label>
                      <input
                        type='date'
                        value={newEvent.date}
                        onChange={(e) =>
                          setNewEvent({ ...newEvent, date: e.target.value })
                        }
                        className='w-full bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 text-white'
                        required
                      />
                    </div>
                    <div>
                      <label className='block text-sm font-medium mb-2 text-blue-300'>
                        Heure
                      </label>
                      <input
                        type='time'
                        value={newEvent.time}
                        onChange={(e) =>
                          setNewEvent({ ...newEvent, time: e.target.value })
                        }
                        className='w-full bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 text-white'
                        required
                      />
                    </div>
                  </div>

                  <div className='flex flex-col sm:flex-row justify-end gap-3 mt-8'>
                    {selectedEvent && selectedEvent.type === "intervention" && (
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        type='button'
                        onClick={handleOpenFullIntervention}
                        className='px-4 py-3 bg-green-600 hover:bg-green-700 rounded-lg transition-colors flex items-center justify-center gap-2'>
                        <ExternalLink className='w-4 h-4' />
                        <span>Voir l'intervention complète</span>
                      </motion.button>
                    )}
                    {selectedEvent && (
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        type='button'
                        onClick={() => handleDeleteEvent(selectedEvent.id)}
                        className='px-4 py-3 bg-red-600 hover:bg-red-700 rounded-lg transition-colors flex items-center justify-center gap-2'>
                        <Trash2 className='w-4 h-4' />
                        <span>Supprimer</span>
                      </motion.button>
                    )}
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      type='button'
                      onClick={() => setShowEventModal(false)}
                      className='px-4 py-3 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors'>
                      Annuler
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      type='submit'
                      className={`px-4 py-3 rounded-lg transition-colors ${
                        eventType === "intervention" ?
                          "bg-blue-600 hover:bg-blue-700"
                        : "bg-purple-600 hover:bg-purple-700"
                      }`}>
                      {selectedEvent ? "Mettre à jour" : "Ajouter"}
                    </motion.button>
                  </div>
                </form>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        <style>{`
          .custom-scrollbar::-webkit-scrollbar {
            width: 6px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: rgba(30, 41, 59, 0.5);
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: rgba(109, 40, 217, 0.3);
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: rgba(109, 40, 217, 0.5);
          }
        `}</style>
      </div>
    </motion.div>
  );
};

export default Calendar;